/**
 * Events (Event Manager) → site-wide table export source: the whole schedule
 * that matches the page's search / season / type / status filters, in season
 * order (oldest first — the order a schedule is read), one row per event, with
 * event name, opponent, date, type, status, play count and current score.
 *
 * Play counts are read from the Play Archive listing (`GET /plays/v1`,
 * `limit=1` → `total`) — one small request per event, in parallel.
 */
import type { ApiClient } from '../../lib/api-client';
import type { TeamEvent } from '../../lib/api-types';
import { EVENT_STATUS_LABELS, EVENT_TYPE_LABELS, FILM_REVIEW_LABEL } from '../../lib/event-format';
import type { ExportCell, ExportColumnDef, TableExportSource } from '../integrations/export-dialog';
import { exportFileStem, fetchAllPages, fileDay } from '../integrations/export-format';

import type { EventQueryState } from './use-events';

const COLUMNS: readonly ExportColumnDef[] = [
  { field: 'name', label: 'Event', header: 'Event name', isVisible: true },
  { field: 'opponent', label: 'Opponent', isVisible: true },
  { field: 'date', label: 'Date', isVisible: true },
  { field: 'time', label: 'Kickoff time', isVisible: true },
  { field: 'event_type', label: 'Type', header: 'Event type', isVisible: true },
  { field: 'status', label: 'Status', isVisible: true },
  { field: 'play_count', label: 'Play count', header: 'Plays', isVisible: true },
  { field: 'score', label: 'Current score', header: 'Score', isVisible: true },
  { field: 'season', label: 'Season', isVisible: true },
  { field: 'location', label: 'Location', isVisible: true },
  { field: 'home_score', label: 'Home score', isVisible: false },
  { field: 'away_score', label: 'Away score', isVisible: false },
];

function localDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : fileDay(iso);
}

function localTime(iso: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export function eventRowCells(event: TeamEvent, playCount: number | null): ExportCell[] {
  const hasScore = event.home_score !== null || event.away_score !== null;
  return [
    event.name,
    event.opponent_name,
    localDate(event.scheduled_at),
    localTime(event.scheduled_at),
    EVENT_TYPE_LABELS[event.event_type],
    event.status === 'closed'
      ? `${EVENT_STATUS_LABELS.closed} (${FILM_REVIEW_LABEL})`
      : EVENT_STATUS_LABELS[event.status],
    playCount,
    hasScore ? `${event.home_score ?? 0} – ${event.away_score ?? 0}` : null,
    event.season_year,
    event.location,
    event.home_score,
    event.away_score,
  ];
}

export async function buildEventsExportSource(
  api: ApiClient,
  query: EventQueryState
): Promise<TableExportSource> {
  const events = await fetchAllPages(
    (page, limit) =>
      api.listEvents({
        q: query.search === '' ? undefined : query.search,
        season: query.season === 'all' ? undefined : Number(query.season),
        type: query.type === 'all' ? undefined : query.type,
        status: query.status === 'all' ? undefined : query.status,
        // Season order: a schedule reads oldest first.
        sort_by: 'scheduled_at',
        sort_order: 'asc',
        page,
        limit,
      }),
    200
  );
  const counts = await Promise.all(
    events.map((event) =>
      api
        .listPlays({ event_id: event.event_id, limit: 1, page: 1 })
        .then((page) => page.total)
        .catch(() => null)
    )
  );
  const season = query.season === 'all' ? null : `${query.season}_season`;
  return {
    page: 'events',
    pageLabel: 'Events',
    columns: COLUMNS,
    rows: () => events.map((event, index) => eventRowCells(event, counts[index] ?? null)),
    fileStem: exportFileStem('Events', season, fileDay(null)),
    sheetName: 'Schedule',
    rowNoun: { one: 'event', many: 'events' },
  };
}
